import React from "react";
import { ChevronDown, ChevronRight, LoaderCircle } from "lucide-react";
import ConsolidationPopover from "../consolidation/ConsolidationPopover.jsx";
import CollapsedDaysGuide from "./CollapsedDaysGuide.jsx";
import { Field, INPUT, PRIMARY } from "../consolidation/Fields.jsx";
import { billableCalculationLabel, billableHoursTotal, billingNumber, formatBillingMoney, formatBillingNumber, formatBillingQuantity } from "../../utils/billingCalculation.js";
import { approx, commonShare, continuesFrom, excludedSeconds, groupBillableCalculationDays, kmLabel, STOP_KINDS, stopDeparturePlace, summarizeBillableTime, tonsLabel } from "../../utils/billingCalculationDays.js";
import { buildInvoiceOrderRows } from "../../utils/billingOrderRows.js";
import { DELIVERY_COLOR, LOADING_COLOR } from "../consolidation/palette.js";
import { invoiceRowTourKey, normalizedSearch } from "../../utils/billingInvoiceGroups.js";

// Journée de chaque camion racontée arrêt par arrêt, comme « Expliquer au client » de À facturer, avec les tarifs de chaque commande.
const duration = (seconds) => {
  if (seconds == null) return "—";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}`;
};
// « 19 octobre », « 1er octobre » : date d'une ligne libre.
const dayMonth = (date) => new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "long", timeZone: "UTC" })
  .format(new Date(`${date}T12:00:00Z`)).replace(/^1 /, "1er ");
const dayLabel = (date) => new Intl.DateTimeFormat("fr-CA", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" })
  .format(new Date(`${date}T12:00:00Z`)).replace(/^./, (letter) => letter.toUpperCase());
const clock = (value, zone) => value ? new Intl.DateTimeFormat("fr-CA", { hour: "numeric", minute: "2-digit", timeZone: zone || "America/Toronto" }).format(new Date(value)) : "";
const TH = "px-2 py-1 font-extrabold";
const TD = "px-2 py-0.5 align-top leading-[14px]";
// Pastilles pleines de la Consolidation (comme ses segments du Gantt) : chargement bleu, livraison ambre ; temps commun en gris.
const STOP_COLORS = { loading: [LOADING_COLOR, "#fff"], preload: [LOADING_COLOR, "#fff"], delivery: [DELIVERY_COLOR, "#1f1606"], dompeur: [DELIVERY_COLOR, "#1f1606"],
  common: ["#e2e8f0", "#334155"], extra: ["#e2e8f0", "#334155"] };
const COLUMNS = 9;
const RATES = [
  { field: "hourly_rate_override", label: "$/h" },
  { field: "loading_tonne_rate_override", label: "$/t" },
  { field: "fixed_rate_override", label: "$ fixe" },
];

function RatesEditor({ row, currency, disabled, onApply }) {
  // Camion dompeur : le $/h n'a pas d'effet, comme dans le relevé du chauffeur.
  const rates = row.dompeur ? RATES.filter(({ field }) => field !== "hourly_rate_override") : RATES;
  // Sans exception, un champ reste vide (« habituel »), le $/t compris.
  const initial = { hourly_rate_override: row.hourlyException ? String(row.hourly) : "",
    loading_tonne_rate_override: row.tonneException && row.tonneRate != null ? String(row.tonneRate) : "",
    fixed_rate_override: row.fixedException && row.fixed != null ? String(row.fixed) : "" };
  const [values, setValues] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const changed = Object.fromEntries(rates.filter(({ field }) => values[field].trim() !== initial[field])
    .map(({ field }) => [field, values[field].trim() === "" ? null : values[field].trim()]));
  const exceptional = row.hourlyException || row.fixedException || row.tonneException;
  return <ConsolidationPopover title={`Tarifs · ${row.reference}`} panelWidth="24rem" disabled={disabled} tooltip={row.dompeur ? "Modifier $/t ou $ fixe" : "Modifier $/h, $/t ou $ fixe"}
    className={`inline-flex h-5 items-center rounded-[4px] border px-1 text-[11px] font-extrabold focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50 ${exceptional
      ? "border-amber-500 bg-amber-100 text-amber-950 hover:border-amber-600" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"}`}
    label={<><span className="sr-only">Modifier les tarifs de {row.reference}</span><span aria-hidden="true">$</span></>}>
    {({ close }) => <form className="grid gap-2" onSubmit={async (event) => {
      event.preventDefault();
      setSaving(true);
      try {
        if (await onApply(row.orderId, changed)) close();
      } finally { setSaving(false); }
    }}>
      <div className={`grid gap-2 ${row.dompeur ? "grid-cols-2" : "grid-cols-3"}`}>
        {rates.map(({ field, label }) => <Field key={field} label={label}>
          <input className={`${INPUT} !h-7 !py-0`} type="number" min="0" step="0.0001" value={values[field]} disabled={saving}
            placeholder={field === "hourly_rate_override" && row.hourly != null ? formatBillingNumber(row.hourly, 2) : "habituel"}
            aria-label={`${row.reference} · ${label}`} onChange={(event) => setValues((current) => ({ ...current, [field]: event.target.value }))} />
        </Field>)}
      </div>
      <p className="text-xs text-slate-500">Champ vide : tarif habituel ; zéro : exception à zéro.{row.dompeur ? "" : " Le $/h s’applique à la livraison ; le chargement, l’inspection et le retour gardent le tarif normal."}</p>
      <p className="text-xs text-slate-500">Le relevé Consolidation de cette commande est rouvert puis revalidé avec ce tarif{row.preloaded ? ", ainsi que celui du Chargé PM" : ""}. Le montant actuel est de {formatBillingMoney(row.amount, currency)}.</p>
      <div className="flex items-center justify-end gap-2">
        {saving ? <span className="inline-flex items-center gap-1 text-xs text-slate-600"><LoaderCircle size={13} className="animate-spin" aria-hidden="true" />Revalidation du relevé… (jusqu’à 30 s)</span> : null}
        <button type="submit" className={PRIMARY} disabled={saving || !Object.keys(changed).length}>Appliquer</button>
      </div>
    </form>}
  </ConsolidationPopover>;
}

const sumCents = (items) => items.reduce((total, item) => total + Math.round((billingNumber(item.line_total) ?? 0) * 100), 0);
const orderOf = (item) => item.presentation_context?.order_id || null;
const hoursLabel = (hours) => duration(hours == null ? null : hours * 3600);

function StopName({ kind, children }) {
  const [background, color] = STOP_COLORS[kind];
  return <div>
    <span className="mr-1.5 inline-block min-w-[78px] rounded-[4px] px-1 text-center text-[9px] font-semibold uppercase leading-3" style={{ background, color }}>
      {kind === "common" ? "Inspection et retour" : STOP_KINDS[kind] || "Autres frais"}</span>{" "}
    <span className="text-slate-800">{children}</span>
  </div>;
}

// « Comptoir Agricole à 13 h 07 », « après une livraison · autre client à 11 h 45 » ou seulement l'heure.
function departure(step, zone, ctx) {
  const from = stopDeparturePlace(step, ctx.ownSites);
  const start = clock(step.block?.start, zone);
  return { from, text: [from, start ? `à ${start}` : ""].filter(Boolean).join(" ") };
}

function RouteCell({ seconds, km, estimated = false, className = TD }) {
  return <td className={`${className} whitespace-nowrap text-right tabular-nums`}>{seconds == null ? "" : `${approx(estimated)}${duration(seconds)}`}{seconds != null && km != null ? <span className="text-slate-500"> · {kmLabel(km, 1)}</span> : null}</td>;
}

function RateCell({ orderId, ctx }) {
  const row = orderId ? ctx.rows.get(orderId) : null;
  return <td className={`${TD} w-8 text-right`}>{row && ctx.canEdit ? <RatesEditor row={row} currency={ctx.currency} disabled={ctx.busy} onApply={ctx.onApply} /> : null}</td>;
}

function OrderRow({ item, ctx }) {
  // Tonnage de la commande, sinon sa part du tonnage livré à l'arrêt.
  const tons = billingNumber(item.presentation_context?.tonnage) ?? billingNumber(item.calculation_details?.coefficient?.numerator);
  return <tr className="text-[11.5px] text-slate-700">
    <th scope="row" className={`${TD} pl-6 text-left font-normal`}><span className="font-extrabold text-accent">{item.order_reference}</span>{tons === null ? "" : ` · ${tonsLabel(tons)}`}</th>
    <td colSpan={5} /><td className={`${TD} text-right tabular-nums`}>{billableHoursTotal([item]) === undefined ? "" : hoursLabel(billableHoursTotal([item]))}</td>
    <td className={`${TD} whitespace-nowrap text-right tabular-nums`}>{formatBillingMoney(billingNumber(item.line_total), ctx.currency)}</td>
    <RateCell orderId={orderOf(item)} ctx={ctx} />
  </tr>;
}

function StopRows({ step, previous, zone, ctx }) {
  const orders = step.items.filter(orderOf);
  const single = orders.length === 1 ? orders[0] : null;
  const excluded = excludedSeconds(step.block);
  const detail = step.kind === "loading" ? [step.tons === null ? "" : `${tonsLabel(step.tons)} chargées`]
    : [orders.length > 1 ? `${orders.length} cmd` : "", tonsLabel(step.tons)];
  const parts = [...detail, excluded ? `${duration(excluded)} exclues` : ""].filter(Boolean);
  // Le départ accompagne son arrêt ; ne pas répéter celui qui reprend l'arrêt du dessus.
  const { from, text } = departure(step, zone, ctx);
  return <>
    <tr className="text-xs">
      <th scope="row" className={`${TD} text-left font-normal`}><StopName kind={step.kind}>
        <b className="font-extrabold">{step.site}</b>
        {single ? <> · <span className="font-extrabold text-accent">{single.order_reference}</span></> : null}
        {parts.map((part) => <span key={part} className="text-slate-500"> · {part}</span>)}
      </StopName></th>
      <td className={`${TD} max-w-[180px] text-[11px] text-slate-500`}>{!continuesFrom(step, previous, from) ? text : ""}</td>
      <RouteCell seconds={step.travel} km={step.km} estimated={step.estimated} />
      <td className={`${TD} text-right tabular-nums`}>{clock(step.block?.arrival, zone)}</td>
      <td className={`${TD} text-right tabular-nums`}>{step.onsite === null ? "" : `${approx(step.estimated)}${duration(step.onsite)}`}</td>
      <td className={`${TD} text-right tabular-nums`}>{clock(step.block?.end, zone)}</td>
      <td className={`${TD} text-right font-bold tabular-nums`}>{step.seconds === null ? formatBillingQuantity(step.items[0].quantity, step.items[0].billing_unit) : duration(step.seconds)}</td>
      <td className={`${TD} whitespace-nowrap text-right font-extrabold tabular-nums`}>{formatBillingMoney(sumCents(step.items) / 100, ctx.currency)}</td>
      <RateCell orderId={single && orderOf(single)} ctx={ctx} />
    </tr>
    {orders.length > 1 ? orders.map((item) => <OrderRow key={item.id} item={item} ctx={ctx} />) : null}
  </>;
}

const STOPS = new Set(Object.keys(STOP_KINDS));
const COMMON = new Set(["inspection", "return"]);

function TruckDay({ day, ctx }) {
  const time = summarizeBillableTime(day.items);
  const stops = day.steps.filter((step) => STOPS.has(step.kind));
  const common = day.steps.filter((step) => COMMON.has(step.kind)).flatMap((step) => step.items);
  const extras = day.steps.filter((step) => !STOPS.has(step.kind) && !COMMON.has(step.kind)).flatMap((step) => step.items);
  const share = commonShare(day);
  const onsite = time.loading.seconds + time.delivery.seconds;
  const tourKey = ctx.gps?.enabled ? invoiceRowTourKey(day.items[0]) : null;
  const selected = Boolean(tourKey) && ctx.gps.selected?.key === tourKey;
  const crew = [day.truck ? `Camion ${day.truck}` : "", day.driver].filter(Boolean).join(" · ") || "Camion non indiqué";
  const HEAD = `${TD} border-t border-slate-300 bg-slate-200 py-1 font-extrabold text-slate-800`;
  return <tbody className={`${tourKey ? "cursor-pointer" : ""} ${selected ? "invoice-order-selected bg-neutral-200/60" : tourKey ? "hover:bg-slate-50" : ""}`}
    data-selection-start={selected || undefined} data-selection-end={selected || undefined}
    onClick={tourKey ? () => ctx.gps.select(tourKey, true) : undefined}>
    {/* En-tête du camion : identification et sous-totaux dans leurs colonnes. */}
    <tr className="text-xs">
      <th scope="row" aria-current={selected || undefined} className={`${HEAD} text-left`}>{crew}</th>
      <td className={HEAD} />
      <RouteCell seconds={time.route.count ? time.route.seconds : null} km={time.route.km} estimated={time.route.estimated} className={HEAD} /><td className={HEAD} />
      <td className={`${HEAD} text-right tabular-nums`}>{onsite ? `${approx(time.loading.estimated || time.delivery.estimated)}${duration(onsite)}` : ""}</td><td className={HEAD} />
      <td className={`${HEAD} text-right tabular-nums`}>{hoursLabel(time.hours)}</td>
      <td className={`${HEAD} whitespace-nowrap text-right tabular-nums`}>{formatBillingMoney(sumCents(day.items) / 100, ctx.currency)}</td><td className={HEAD} />
    </tr>
    {stops.map((step, index) => <StopRows key={step.key} step={step} previous={stops[index - 1]} zone={day.timezone} ctx={ctx} />)}
    {common.length ? <tr className="text-xs">
      <th scope="row" className={`${TD} text-left font-normal`}><StopName kind="common"><span className="text-slate-500">
        {share !== null && share < 0.9995 ? `part de ce client : ${Math.round(share * 100)} % de la journée du camion` : "temps du camion"}</span></StopName></th>
      <td colSpan={5} /><td className={`${TD} text-right font-bold tabular-nums`}>{hoursLabel(billableHoursTotal(common))}</td>
      <td className={`${TD} whitespace-nowrap text-right font-extrabold tabular-nums`}>{formatBillingMoney(sumCents(common) / 100, ctx.currency)}</td><td />
    </tr> : null}
    {extras.map((item) => <tr key={item.id} className="text-xs">
      <th scope="row" className={`${TD} text-left font-normal`}><StopName kind="extra">{billableCalculationLabel(item)}{item.order_reference ? <> · <span className="font-extrabold text-accent">{item.order_reference}</span></> : null}</StopName></th>
      <td colSpan={5} /><td className={`${TD} text-right tabular-nums`}>{formatBillingQuantity(item.quantity, item.billing_unit)}</td>
      <td className={`${TD} whitespace-nowrap text-right font-extrabold tabular-nums`}>{formatBillingMoney(billingNumber(item.line_total), ctx.currency)}</td>
      <RateCell orderId={orderOf(item)} ctx={ctx} />
    </tr>)}
  </tbody>;
}

// Un préchargement n'a pas d'arrêt enregistré : sa description dit quand et par qui il a été fait la veille.
function PreloadRows({ items, ctx }) {
  return <tbody>
    <tr><td colSpan={COLUMNS} className="border-t border-slate-300 bg-slate-200 px-2 py-1 text-xs font-extrabold text-slate-800">
      Préchargements<span className="font-semibold text-slate-600"> · {hoursLabel(billableHoursTotal(items))} · {formatBillingMoney(sumCents(items) / 100, ctx.currency)}</span>
    </td></tr>
    {items.map((item) => {
      const orders = [...new Set((item.service_context?.cargo_origins || []).flatMap((origin) => origin.order_references || []))];
      const parts = [tonsLabel(billingNumber(item.presentation_context?.tonnage)), item.description?.split(" · ").slice(2).join(" · ")].filter(Boolean);
      return <tr key={item.id} className="text-xs">
        <th scope="row" className={`${TD} text-left font-normal`}><StopName kind="preload">
          {orders.length ? <span className="font-extrabold text-accent">{orders.join(", ")}</span> : null}
          {parts.map((part, index) => <span key={part} className="text-slate-500">{index || orders.length ? " · " : ""}{part}</span>)}
        </StopName></th>
        <td colSpan={5} /><td className={`${TD} text-right font-bold tabular-nums`}>{hoursLabel(billableHoursTotal([item]))}</td>
        <td className={`${TD} whitespace-nowrap text-right font-extrabold tabular-nums`}>{formatBillingMoney(billingNumber(item.line_total), ctx.currency)}</td><td />
      </tr>;
    })}
  </tbody>;
}

const searchText = (day) => [day.truck, day.driver, ...day.steps.map((step) => step.site),
  ...day.items.flatMap((item) => [item.order_reference, item.presentation_context?.client_name])].join(" ");

/** Journées des camions d'un brouillon de relevé, arrêt par arrêt ; `day` et `query` viennent des filtres du résumé. */
export default function InvoiceOrderLines({ lines, currency, view, day = null, query = "", canEdit = false, busy = false, onApply, gps = null }) {
  const model = React.useMemo(() => {
    const sheet = lines.filter((line) => line && !line.is_manual && line.calculation_details?.revision_id);
    const preloads = sheet.filter((line) => line.calculation_details.kind === "preload");
    return { trucks: groupBillableCalculationDays(sheet.filter((line) => !preloads.includes(line))), preloads,
      other: lines.filter((line) => line && !sheet.includes(line)),
      // Le nom d'un autre client n'est jamais présenté : un départ n'est nommé que s'il est un lieu de ce relevé.
      ownSites: new Set(sheet.flatMap((line) => (line.service_context?.destination || []).map((location) => location.name)).filter(Boolean)),
      rows: new Map(buildInvoiceOrderRows(lines).days.flatMap((item) => item.rows).map((row) => [row.orderId, row])) };
  }, [lines]);
  const ctx = { currency, canEdit, busy, onApply, gps, rows: model.rows, ownSites: model.ownSites };
  const firstDay = React.useRef(null);
  const needle = normalizedSearch(query);
  const visible = (date, text) => (!day || date === day) && (!needle || normalizedSearch(text).includes(needle));
  const trucks = model.trucks.filter((item) => visible(item.date, searchText(item)));
  const preloads = model.preloads.filter((item) => visible(item.service_date, item.description || ""));
  const dates = [...new Set([...preloads.map((item) => item.service_date), ...trucks.map((item) => item.date)])].sort().map((date) => {
    const items = trucks.filter((item) => item.date === date);
    const loaded = preloads.filter((item) => item.service_date === date);
    const all = [...loaded, ...items.flatMap((item) => item.items)];
    return { date, items, preloads: loaded, orders: new Set(all.map(orderOf).filter(Boolean)).size, cents: sumCents(all), hours: billableHoursTotal(all) };
  });
  return <div className="overflow-x-auto">
    <table className="w-full min-w-[900px] text-left text-xs">
      <thead className="bg-neutral-700 text-[10px] uppercase tracking-wide text-white [&_th]:bg-neutral-700 [&_th]:text-white">
        <tr className="border-b-2 border-neutral-600">
          <th scope="col" className={`${TH} w-[34%]`}>Arrêt</th><th scope="col" className={TH}>Départ</th><th scope="col" className={`${TH} text-right`}>Route</th><th scope="col" className={`${TH} text-right`}>Arrivée</th>
          <th scope="col" className={`${TH} text-right`}>Sur place</th><th scope="col" className={`${TH} text-right`}>Départ de l’arrêt</th><th scope="col" className={`${TH} text-right`}>Heures</th>
          <th scope="col" className={`${TH} text-right`}>Montant</th><th scope="col" className={TH}><span className="sr-only">Tarifs</span></th>
        </tr>
      </thead>
      {dates.map((item, index) => <React.Fragment key={item.date}>
        <tbody><tr><td colSpan={COLUMNS} className="border-t border-slate-300 bg-slate-100 p-0 text-xs font-extrabold text-slate-800">
          <button ref={index ? undefined : firstDay} type="button" aria-expanded={view.isDayOpen(item.date)} onClick={() => view.toggleDay(item.date)}
            className="flex w-full flex-wrap items-center gap-1 rounded-[4px] px-2 py-1 text-left hover:bg-slate-200 focus-visible:outline-2 focus-visible:outline-accent">
            {view.isDayOpen(item.date) ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
            <span>{dayLabel(item.date)}</span><span className="ml-2 font-semibold text-slate-500">{[item.items.length ? `${item.items.length} camion${item.items.length > 1 ? "s" : ""}` : "",
              item.preloads.length ? `${item.preloads.length} préchargement${item.preloads.length > 1 ? "s" : ""}` : "", item.orders ? `${item.orders} commande${item.orders > 1 ? "s" : ""}` : "", hoursLabel(item.hours), formatBillingMoney(item.cents / 100, currency)].filter(Boolean).join(" · ")}</span>
          </button>
        </td></tr></tbody>
        {view.isDayOpen(item.date) ? <>
          {item.preloads.length ? <PreloadRows items={item.preloads} ctx={ctx} /> : null}
          {item.items.map((truck) => <TruckDay key={truck.key} day={truck} ctx={ctx} />)}
        </> : null}
      </React.Fragment>)}
      {!dates.length ? <tbody><tr><td colSpan={COLUMNS} className="border-t border-slate-200 px-2 py-2 text-xs text-slate-500">Aucune commande ne correspond à la recherche.</td></tr></tbody> : null}
      {model.other.length && !day && !needle ? <tbody>
        <tr><td colSpan={COLUMNS} className="border-t border-slate-300 bg-slate-100 px-2 py-1 text-xs font-extrabold text-slate-800">Lignes libres</td></tr>
        {model.other.map((line, index) => <tr key={line.id || index} className="border-t border-slate-200 text-xs">
          <td className={TD} colSpan={COLUMNS - 2}>{line.description || "—"}{line.service_date ? <span className="text-slate-500"> · {dayMonth(line.service_date)}</span> : null}</td>
          <td className={`${TD} whitespace-nowrap text-right font-extrabold tabular-nums`}>{formatBillingMoney(line.line_total, currency)}</td><td />
        </tr>)}
      </tbody> : null}
    </table>
    <CollapsedDaysGuide target={firstDay} controls={view.guideControls} started={view.guideStarted} days={dates.length}
      active={dates.length > 0 && !dates.some((item) => view.isDayOpen(item.date))} />
  </div>;
}
